import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx'
import { ChevronDown, X } from 'lucide-react'
import { Fragment } from 'react'

type Option = {
  label: string
  value: string
  logo?: React.ReactNode
}

type BaseMultiSelectProps = {
  name?: string
  value?: string[]
  options: Option[]
  onChange?: (value: string[]) => void
  className?: string
  size?: 'sm' | 'md'
  label?: string
  error?: string
  disabled?: boolean
  placeholder?: string
}

export const BaseMultiSelect = ({
  value = [],
  options,
  onChange,
  className = '',
  size = 'sm',
  label,
  disabled,
  placeholder = 'Выберите...',
}: BaseMultiSelectProps) => {
  const selected = options.filter((opt) => value.includes(opt.value))

  const handleChange = (selectedOptions: Option[]) => {
    onChange?.(selectedOptions.map((opt) => opt.value))
  }

  const removeSelection = (option: Option) => {
    const newSelected = selected.filter((sel) => sel.value !== option.value)
    onChange?.(newSelected.map((opt) => opt.value))
  }

  const variantClasses = size === 'sm' ? 'rounded-lg px-3 py-2' : 'rounded-2xl px-4 py-3'

  return (
    <div className={`flex flex-col gap-2 relative ${className}`}>
      {label && <div className="text-text-main dark:text-white">{label}</div>}
      <Listbox value={selected} onChange={handleChange} multiple disabled={disabled}>
        <div>
          <ListboxButton
            className={`relative w-full text-left text-text-main dark:text-white cursor-pointer border-0 ring ring-card-border dark:ring-card-border-dark focus:ring-2 focus:ring-card-border dark:focus:ring-card-border-dark placeholder-text-secondary disabled:cursor-not-allowed disabled:opacity-75 ${variantClasses}`}
          >
            <div className="flex flex-wrap gap-1">
              {selected.length > 0 ? (
                selected.map((sel) => (
                  <span
                    key={sel.value}
                    className="flex items-center bg-light-purple px-2 py-0.5 dark:bg-card-border-dark rounded-sm text-sm"
                  >
                    {sel.label}
                    <span
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeSelection(sel)
                      }}
                    >
                      <X className="w-4 h-4" />
                    </span>
                  </span>
                ))
              ) : (
                <span className="text-gray-400">{placeholder}</span>
              )}
            </div>
            <ChevronDown className="top-1/2 right-3 absolute w-5 h-5 -translate-y-1/2 transform" />
          </ListboxButton>

          <ListboxOptions
            anchor="bottom"
            transition
            className={clsx(
              'bg-white dark:bg-dark-bg shadow-lg mt-1 ring-card-border dark:ring-card-border-dark rounded-lg focus:outline-none ring-1 w-[var(--button-width)] list-none [--anchor-gap:var(--spacing-1)]',
              'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
              'h-[140px] overflow-y-auto custom-scrollbar rounded-sm'
            )}
          >
            {options.map((option, idx) => (
              <ListboxOption key={idx} value={option} as={Fragment}>
                {({ selected }) => (
                  <li
                    className={`cursor-pointer select-none relative px-4 py-2 ${
                      selected
                        ? 'text-primary dark:text-primary-dark bg-light-purple dark:bg-dark-purple'
                        : 'text-text-secondary'
                    }`}
                  >
                    {option.logo && (
                      <span className="top-1/2 left-3 absolute -translate-y-1/2 transform">
                        {option.logo}
                      </span>
                    )}
                    <span className={option.logo ? 'pl-6' : ''}>{option.label}</span>
                  </li>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  )
}
